import React, { useState, useEffect } from 'react';
import {
    Box, Badge, Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Input,
    HStack, VStack, Image, Flex, Icon, useToast, Divider, Spinner
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { FaWarehouse, FaImage } from 'react-icons/fa';
import { incluirCanceladaNoEstoque, getSugestoesLocalizacaoDespacho } from '../../services/api';
import ListaCompraStatusBadge from '../ListaCompraStatusBadge';
import { BadgesStatusVenda, DatasDespacho } from './StatusVendaMeta';

const CardVendaCancelada = ({ venda, userId, onIncluido, onCorrigirImagem }) => {
    const toast = useToast();
    const [selectedItem, setSelectedItem] = useState(null);
    const [qtd, setQtd] = useState('');
    const [loc, setLoc] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [sugestoes, setSugestoes] = useState([]);
    const [loadingSugestoes, setLoadingSugestoes] = useState(false);
    const [itensIncluidos, setItensIncluidos] = useState(
        () => new Set(venda.itens.filter(i => i.estoque_devolvido_at).map(i => i.produto_estoque_id))
    );
    const itensFuro = new Set(venda.itens.filter(i => i.furo_estoque_taga_at).map(i => i.produto_estoque_id));

    // Peca que o cancelamento ja devolveu ao estoque sozinha. Antes o operador
    // nao tinha como saber disso e dava entrada por cima, somando em dobro.
    const retornoAutomatico = new Map(
        venda.itens.filter(i => i.retorno_automatico_em).map(i => [i.produto_estoque_id, i.retorno_automatico_em])
    );
    const totalResolvidos = venda.itens.filter(
        i => itensIncluidos.has(i.produto_estoque_id) || retornoAutomatico.has(i.produto_estoque_id)
    ).length;
    const tudoResolvido = totalResolvidos === venda.itens.length;

    useEffect(() => {
        if (!selectedItem?.produto_estoque_id) return;
        let cancelado = false;
        setLoadingSugestoes(true);
        getSugestoesLocalizacaoDespacho(selectedItem.produto_estoque_id)
            .then(lista => { if (!cancelado) setSugestoes(lista); })
            .finally(() => { if (!cancelado) setLoadingSugestoes(false); });
        return () => { cancelado = true; };
    }, [selectedItem?.produto_estoque_id]);

    const handleOpenModal = (item) => {
        setSelectedItem(item);
        setQtd(String(item.quantidade));
        setLoc('');
        setSugestoes([]);
    };

    const handleClose = () => setSelectedItem(null);

    const handleIncluir = async () => {
        const qtdNum = parseInt(qtd);
        if (!qtdNum || qtdNum < 1 || !loc.trim()) {
            toast({ title: 'Preencha quantidade e localização', status: 'warning', isClosable: true });
            return;
        }
        setSalvando(true);
        const res = await incluirCanceladaNoEstoque({
            venda_id: venda.venda_id,
            produto_estoque_id: selectedItem.produto_estoque_id,
            qtd: qtdNum,
            loc: loc.trim(),
            userId,
        });
        setSalvando(false);
        if (res?.success) {
            toast({ title: 'Item incluído no estoque', status: 'success', duration: 2500, isClosable: true });
            setItensIncluidos(prev => new Set([...prev, selectedItem.produto_estoque_id]));
            setSelectedItem(null);
            if (onIncluido) onIncluido(venda.venda_id, selectedItem.produto_estoque_id);
        } else {
            toast({ title: res?.error || 'Erro ao incluir no estoque', status: 'error', isClosable: true });
        }
    };

    return (
        <>
            <Box
                borderWidth="2px"
                borderColor={tudoResolvido ? 'green.300' : 'red.300'}
                borderRadius="xl"
                bg={tudoResolvido ? 'green.50' : 'red.50'}
                p={4}
                boxShadow="sm"
            >
                <Flex justify="space-between" align="flex-start" mb={2} wrap="wrap" gap={2}>
                    <VStack align="flex-start" spacing={1.5}>
                        <HStack spacing={1} flexWrap="wrap">
                            <Badge colorScheme="red" fontSize="xs" px={2} py={1} borderRadius="full" display="inline-flex" alignItems="center" gap={1}>
                                <WarningIcon boxSize={3} /> VENDA CANCELADA
                            </Badge>
                            <Text fontSize="xs" color="red.600" fontWeight="semibold">{venda.cancelada_at_formatada}</Text>
                        </HStack>
                        <BadgesStatusVenda origem={venda.origem} status={venda.status} statusTaga={venda.status_taga} />
                        <DatasDespacho dataVenda={venda.data_venda} prazoEnvio={venda.prazo_envio} />
                    </VStack>
                    <HStack fontSize="xs" color="gray.600" spacing={3} flexWrap="wrap">
                        <Text>Venda #{venda.venda_id}</Text>
                        {venda.id_origem && <Text>Orig: {venda.id_origem}</Text>}
                        {venda.pack_id && <Text>Pack: {venda.pack_id}</Text>}
                        <Text noOfLines={1}>{venda.cliente}</Text>
                    </HStack>
                </Flex>

                {tudoResolvido && (
                    <Flex align="center" gap={2} bg="green.100" borderRadius="md" px={3} py={2} mb={3}>
                        <CheckCircleIcon color="green.600" />
                        <Text fontSize="xs" fontWeight="bold" color="green.700">
                            Todos os itens desta venda já foram adicionados ao estoque
                        </Text>
                    </Flex>
                )}

                <Divider borderColor={tudoResolvido ? 'green.200' : 'red.200'} mb={3} />

                <VStack spacing={2} align="stretch">
                    {venda.itens.map((item, idx) => {
                        const incluido = itensIncluidos.has(item.produto_estoque_id);
                        const voltouSozinho = retornoAutomatico.get(item.produto_estoque_id);
                        const foiFuro = itensFuro.has(item.produto_estoque_id);
                        const resolvido = incluido || !!voltouSozinho;
                        const borderColor = resolvido ? 'green.200' : (foiFuro ? 'red.300' : 'red.100');
                        return (
                            <Box key={idx} bg="white" borderRadius="lg" p={3}
                                borderWidth="1px" borderColor={borderColor}
                                opacity={resolvido ? 0.75 : 1}>
                                <HStack spacing={3} align="flex-start">
                                    {item.imagem && (
                                        <Image src={item.imagem} alt={item.nome} boxSize="50px"
                                            objectFit="contain" borderRadius="md" flexShrink={0} />
                                    )}
                                    <VStack align="stretch" spacing={1} flex={1} minW={0}>
                                        {item.lista_compra_status && (
                                            <Box alignSelf="flex-start" w="fit-content">
                                                <ListaCompraStatusBadge listaCompraStatus={item.lista_compra_status} />
                                            </Box>
                                        )}
                                        <Text fontSize="sm" fontWeight="bold" color="gray.800" bg="gray.100"
                                            px={2.5} py={1.5} borderRadius="md" lineHeight="tight"
                                            whiteSpace="normal" wordBreak="break-word" w="100%">
                                            {item.nome}
                                        </Text>
                                        <Flex wrap="wrap" columnGap={2} rowGap={1}>
                                            {item.produto_id && (
                                                <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                                    <Text as="span" fontWeight="bold" color="gray.600">SKU:</Text> {item.produto_id}
                                                </Text>
                                            )}
                                            {item.global_code && (
                                                <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                                    <Text as="span" fontWeight="bold" color="gray.600">Global:</Text> {item.global_code}
                                                </Text>
                                            )}
                                            {item.marca && (
                                                <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                                    <Text as="span" fontWeight="bold" color="gray.600">Marca:</Text> {item.marca}
                                                </Text>
                                            )}
                                            {item.codigo_barra && (
                                                <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                                    <Text as="span" fontWeight="bold" color="gray.600">Cód. Barra:</Text> {item.codigo_barra}
                                                </Text>
                                            )}
                                            <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
                                                <Text as="span" fontWeight="bold" color="gray.600">Qtd:</Text> {item.quantidade}
                                            </Text>
                                        </Flex>
                                    </VStack>
                                </HStack>

                                <Flex justify="flex-end" align="center" gap={2} wrap="wrap"
                                    pt={2} mt={2} borderTopWidth="1px" borderColor="gray.100">
                                    {item.imagem_divergente && (
                                        item.imagem_corrigida_at ? (
                                            <Badge colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="md" display="flex" alignItems="center" gap={1}>
                                                <CheckCircleIcon boxSize={3} /> Imagem corrigida
                                            </Badge>
                                        ) : (
                                            <>
                                                <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md" display="flex" alignItems="center" gap={1}>
                                                    <WarningIcon boxSize={3} /> Imagem divergente
                                                </Badge>
                                                {onCorrigirImagem && (
                                                    <Button size="sm" colorScheme="purple" variant="outline" leftIcon={<Icon as={FaImage} />}
                                                        onClick={() => onCorrigirImagem(item, venda)}>
                                                        Corrigir imagem
                                                    </Button>
                                                )}
                                            </>
                                        )
                                    )}
                                    {voltouSozinho ? (
                                        <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="md" display="flex" alignItems="center" gap={1}>
                                            <CheckCircleIcon boxSize={3} /> Voltou ao estoque no cancelamento · {voltouSozinho}
                                        </Badge>
                                    ) : incluido ? (
                                        <Badge colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="md" display="flex" alignItems="center" gap={1}>
                                            <CheckCircleIcon boxSize={3} /> Adicionado{foiFuro ? ' (era furo)' : ''}
                                        </Badge>
                                    ) : (
                                        <>
                                            {foiFuro && (
                                                <Badge colorScheme="red" fontSize="xs" px={2} py={1} borderRadius="md" display="flex" alignItems="center" gap={1}>
                                                    <WarningIcon boxSize={3} /> Furo Estoque
                                                </Badge>
                                            )}
                                            <Button size="sm" colorScheme="orange" leftIcon={<Icon as={FaWarehouse} />}
                                                onClick={() => handleOpenModal(item)}>
                                                Dar entrada em estoque
                                            </Button>
                                        </>
                                    )}
                                </Flex>
                            </Box>
                        );
                    })}
                </VStack>
            </Box>

            <Modal isOpen={!!selectedItem} onClose={handleClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">
                        Adicionar ao Estoque: Venda Cancelada #{venda.venda_id}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedItem && (
                            <VStack spacing={4} align="stretch">
                                <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                                    <Text fontWeight="semibold" fontSize="sm">{selectedItem.nome}</Text>
                                    {selectedItem.global_code && (
                                        <Text fontSize="xs" color="gray.600">Global: {selectedItem.global_code}</Text>
                                    )}
                                    {selectedItem.marca && (
                                        <Text fontSize="xs" color="gray.600">Marca: {selectedItem.marca}</Text>
                                    )}
                                </Box>
                                {itensFuro.has(selectedItem.produto_estoque_id) && (
                                    <Flex align="flex-start" gap={2} bg="orange.50" borderRadius="md" px={3} py={2} borderWidth="1px" borderColor="orange.200">
                                        <WarningIcon color="orange.500" mt={0.5} />
                                        <Text fontSize="xs" color="orange.800">
                                            Este item foi marcado como <strong>furo de estoque</strong> (não localizado fisicamente na conferência). Reintegre ao estoque apenas se o produto realmente existir.
                                        </Text>
                                    </Flex>
                                )}
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">
                                        Quantidade <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal">(máx. {selectedItem.quantidade})</Text>
                                    </FormLabel>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={selectedItem.quantidade}
                                        value={qtd}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value, 10);
                                            if (isNaN(v) || v < 1) { setQtd('1'); return; }
                                            if (v > selectedItem.quantidade) { setQtd(String(selectedItem.quantidade)); return; }
                                            setQtd(String(v));
                                        }}
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Localização no estoque</FormLabel>
                                    {loadingSugestoes ? (
                                        <Flex align="center" gap={2} mb={2}>
                                            <Spinner size="xs" color="blue.400" />
                                            <Text fontSize="xs" color="gray.500">Buscando localizações...</Text>
                                        </Flex>
                                    ) : sugestoes.length > 0 && (
                                        <Box bg="blue.50" borderRadius="md" p={3} mb={2} borderWidth="1px" borderColor="blue.100">
                                            <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>
                                                Produto já em estoque nos locais:
                                            </Text>
                                            <HStack flexWrap="wrap" spacing={2}>
                                                {sugestoes.map((s, i) => (
                                                    <Button
                                                        key={i}
                                                        size="xs"
                                                        colorScheme={loc === s.localizacao ? 'green' : 'blue'}
                                                        variant={loc === s.localizacao ? 'solid' : 'outline'}
                                                        onClick={() => setLoc(s.localizacao)}
                                                    >
                                                        {s.localizacao}: {s.quantidade} un
                                                    </Button>
                                                ))}
                                            </HStack>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Clique para alocar na mesma localização
                                            </Text>
                                        </Box>
                                    )}
                                    <Input
                                        placeholder="Ex: A1, B2, Prateleira 3..."
                                        value={loc}
                                        onChange={(e) => setLoc(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleIncluir()}
                                    />
                                </FormControl>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={handleClose}>Cancelar</Button>
                        <Button colorScheme="orange" onClick={handleIncluir} isLoading={salvando}>
                            Incluir no Estoque
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default CardVendaCancelada;
